import * as RadixSlot from '@radix-ui/react-slot';
import cn from 'classnames';

import { KEYBOARD_CODES } from '../../../../constants';
import { CardMedia, type CardMediaProps } from '../shared/CardMedia';
import basicCardStyles from '../shared/styles.module.css';

import {
  ContentSubtitle,
  type StandardCardContentSubtitleProps,
} from './ContentSubtitle';
import {
  ContentTitle,
  type StandardCardContentTitleProps,
} from './ContentTitle';
import { StandardCardSharedPropsContext } from './context';
import styles from './StandardCard.module.css';
import {
  StandardCardContent,
  type StandardCardContentProps,
} from './StandardCardContent';
import type { StandardCardProps } from './types';

type StandardCardComponent = React.FC<StandardCardProps> & {
  Media: React.FC<CardMediaProps>;
  Content: React.FC<StandardCardContentProps>;
  ContentTitle: React.FC<StandardCardContentTitleProps>;
  ContentSubtitle: React.FC<StandardCardContentSubtitleProps>;
};

/**
 * StandardCard is a component that renders a bordered card with proper stylings and the following sections:
 * - Media section
 * - Content section
 *   - Title
 *     - Icon
 *     - Text
 *     - Custom content (like a Badge)
 *   - Subtitle
 *   - Custom content (optional)
 *
 * Common use cases:
 * - rendering Tool card on the Home page (website)
 * - rendering Folder item on the Projects page (website)
 *
 * **⚠️ Important:**
 *
 * Currently, the card `ContentTitle` and `ContentSubtitle` components are **not responsive** based on the breakpoints.
 * This should be added later by creating Typography css mixins
 */
export const StandardCard: StandardCardComponent = ({
  dataTestId,
  className,
  asChild,
  children,
  isActive,
  disabled,
  layout,
  onClick,
  role,
  ...rest
}) => {
  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    // Trigger onClick for keyboard users
    if (
      onClick &&
      (event.key === KEYBOARD_CODES.ENTER ||
        event.key === KEYBOARD_CODES.NUMPAD_ENTER)
    ) {
      event.preventDefault();
      onClick();
    }
  };

  const isInteractive = !disabled && onClick;

  const Component = asChild ? RadixSlot.Root : 'div';
  const content = asChild ? (
    <RadixSlot.Slottable>{children}</RadixSlot.Slottable>
  ) : (
    children
  );

  const cardClassName = cn(
    className,
    styles['standard-card'],
    styles[`standard-card-layout-${layout ?? 'unset'}`],
    isInteractive && styles['standard-card-interactive'],
    basicCardStyles.card
  );

  return (
    <StandardCardSharedPropsContext.Provider value={{ layout, disabled }}>
      <Component
        aria-disabled={disabled ? 'true' : undefined} // Convey the disabled state
        aria-selected={!disabled && isActive} // Convey the active/selected state in case not disabled
        className={cardClassName}
        data-testid={dataTestId}
        role={role}
        tabIndex={isInteractive ? 0 : -1}
        onClick={!disabled ? onClick : undefined} // Ensure disabled state prevents click
        onKeyDown={!disabled && onClick ? handleKeyDown : undefined}
        {...rest}
      >
        {content}
      </Component>
    </StandardCardSharedPropsContext.Provider>
  );
};

StandardCard.Media = CardMedia;
StandardCard.Content = StandardCardContent;
StandardCard.ContentTitle = ContentTitle;
StandardCard.ContentSubtitle = ContentSubtitle;
